/** Cover-banner artwork as SVG data URLs, so presets take the same path as uploaded images. */

import type { Color } from "@salestrak/ui";

export type BannerPreset = {
  id: string;
  label: string;
  preview: string;
  url: string;
  color: Color;
};

function solidBanner(
  id: string,
  label: string,
  color: string,
  palette: Color,
): BannerPreset {
  return {
    id,
    label,
    color: palette,
    preview: `linear-gradient(${color}, ${color})`,
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="500" viewBox="0 0 1600 500"><rect width="1600" height="500" fill="${color}"/></svg>`,
    )}`,
  };
}

function gradientBanner(
  id: string,
  label: string,
  colors: [start: string, end: string, middle?: string],
  palette: Color,
  angle = 135,
): BannerPreset {
  const [start, end, middle] = colors;
  const stops = middle
    ? `<stop offset="0" stop-color="${start}"/><stop offset=".5" stop-color="${middle}"/><stop offset="1" stop-color="${end}"/>`
    : `<stop offset="0" stop-color="${start}"/><stop offset="1" stop-color="${end}"/>`;
  const previewStops = middle
    ? `${start} 0%, ${middle} 50%, ${end} 100%`
    : `${start} 0%, ${end} 100%`;

  return {
    id,
    label,
    color: palette,
    preview: `linear-gradient(${angle}deg, ${previewStops})`,
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="500" viewBox="0 0 1600 500"><defs><linearGradient id="banner" x1="0" y1="0" x2="1" y2="1">${stops}</linearGradient></defs><rect width="1600" height="500" fill="url(#banner)"/></svg>`,
    )}`,
  };
}

/* eslint-disable no-restricted-syntax -- Hexes are artwork shared by both themes, not UI chrome; a token would repaint the user's chosen picture. */
const AQUA_CORAL = gradientBanner(
  "aqua-coral",
  "Aqua and coral",
  ["#7bdad7", "#ef685d", "#c8dfd7"],
  "teal",
);
const SUNSET = gradientBanner(
  "sunset",
  "Sunset",
  ["#ffbf55", "#f01f16"],
  "orange",
);
const OCEAN = gradientBanner(
  "ocean",
  "Ocean",
  ["#117ab2", "#f28a8d", "#28223f"],
  "blue",
);
const BERRY = gradientBanner(
  "berry",
  "Berry",
  ["#272b7a", "#d33f76", "#8749d4"],
  "purple",
);

const GRADIENT_BANNER_PRESETS: readonly BannerPreset[] = [
  AQUA_CORAL,
  SUNSET,
  OCEAN,
  BERRY,
];

export const BANNER_PRESETS: readonly BannerPreset[] = [
  solidBanner("coral", "Coral", "#ef5b62", "red"),
  solidBanner("gold", "Golden yellow", "#f9c252", "yellow"),
  solidBanner("blue", "Sky blue", "#279dcc", "cyan"),
  solidBanner("cream", "Warm cream", "#faeadf", "yellow"),
  solidBanner("purple", "Purple", "#6546e8", "violet"),
  solidBanner("pink", "Pink", "#f5328c", "pink"),
  solidBanner("teal", "Teal", "#22a69a", "teal"),
  solidBanner("orange", "Orange", "#f56a16", "orange"),
  ...GRADIENT_BANNER_PRESETS,
];
/* eslint-enable no-restricted-syntax */
